import { useMutation } from "@tanstack/react-query";

import { api } from "../api";
import { setToken, setUser } from "../utils";
import { ShowToast } from "../../components/toast";
import type { AuthResponse } from "../../types/linked-accounts";

// Persisted across the (full) navigation into onboarding so the onboarding
// shell knows it is running for a freshly-added property and can offer a
// "Cancel & return to original account" affordance. Survives refresh because
// onboarding is multi-step.
export const ADD_PROPERTY_ORIGIN_KEY = "add_property_origin_user_id";

function applyAuth(auth: AuthResponse) {
  const me = auth.linkedAccounts.find((m) => m.isCurrent);
  setToken(auth.accessToken);
  setUser({
    ...auth,
    firstName: me?.userFirstName,
    lastName: me?.userLastName,
    email: me?.userEmail ?? auth.email,
  });
}

// Tell the API client's 401 interceptor to ignore stray 401s during a
// controlled identity swap. Without this, in-flight onboarding requests that
// captured the old token can race the swap and trigger a "Session expired"
// logout. The subsequent hard navigation destroys the page, so we never need
// to clear the flag (mirrors usePropertySwitch).
//
// IMPORTANT: we deliberately do NOT clear this flag on error. Clearing it would
// re-open the logout window: a failed swap followed by any stray 401 (e.g. a
// background refetch) would cascade into a "Session expired" logout. Leaving it
// set keeps the user on the page so they can retry; the next full navigation
// resets it anyway.
function suppressAuthRedirect() {
  (
    window as unknown as { __SUPPRESS_AUTH_REDIRECT__?: boolean }
  ).__SUPPRESS_AUTH_REDIRECT__ = true;
}

function errorMessage(error: unknown): string {
  const err = error as { response?: { data?: { message?: string } } };
  return err?.response?.data?.message || "Something went wrong. Please retry.";
}

/**
 * Create a new property (a new linked user row sharing the email) and swap the
 * session into it, then hard-navigate to onboarding. Only valid for
 * MarketingOnly users — the backend enforces this too.
 */
export function useAddProperty() {
  return useMutation({
    mutationFn: async (hotelName: string): Promise<AuthResponse> => {
      suppressAuthRedirect();
      const res = await api.addProperty(hotelName);
      return res.data as AuthResponse;
    },
    onError: (error) => {
      ShowToast({ type: "error", message: errorMessage(error) });
    },
    onSuccess: (auth, _hotelName, _context) => {
      // Remember where we came from so onboarding can offer "cancel & return".
      const origin = auth.linkedAccounts.find((m) => !m.isCurrent);
      // The previously-active user is the origin. Capture it from the current
      // localStorage user BEFORE we overwrite it.
      const prevUser = (() => {
        try {
          return JSON.parse(localStorage.getItem("user") || "null");
        } catch {
          return null;
        }
      })();
      const originUserId = prevUser?.id ?? origin?.userId ?? null;
      if (originUserId) {
        localStorage.setItem(ADD_PROPERTY_ORIGIN_KEY, originUserId);
      }

      applyAuth(auth);
      // Full navigation so UserProvider / onboarding context reload as the new user.
      window.location.assign("/onboarding");
    },
  });
}

/**
 * Discard the in-progress added property (delete it) and return to the
 * original/sibling account.
 */
export function useCancelAddProperty() {
  return useMutation({
    mutationFn: async (originUserId: string): Promise<AuthResponse> => {
      suppressAuthRedirect();
      const res = await api.cancelAddProperty(originUserId);
      return res.data as AuthResponse;
    },
    onError: (error) => {
      ShowToast({ type: "error", message: errorMessage(error) });
    },
    onSuccess: (auth) => {
      localStorage.removeItem(ADD_PROPERTY_ORIGIN_KEY);
      applyAuth(auth);
      // Navigate to the property's home, NOT "/" (the index route redirects to
      // /login). The origin we return to is a MarketingOnly account.
      window.location.assign("/marketing");
    },
  });
}

/**
 * "Continue later": leave the in-progress property in place (NOT deleted) and
 * switch the session back to a sibling/origin account. The unfinished property
 * stays in the user's account group, so they can resume it any time from the
 * property switcher — onboarding reopens at the step they left.
 */
export function useContinueLater() {
  return useMutation({
    mutationFn: async (targetUserId: string): Promise<AuthResponse> => {
      suppressAuthRedirect();
      const res = await api.switchProperty(targetUserId);
      return res.data as AuthResponse;
    },
    onError: (error) => {
      ShowToast({ type: "error", message: errorMessage(error) });
    },
    onSuccess: (auth) => {
      localStorage.removeItem(ADD_PROPERTY_ORIGIN_KEY);
      applyAuth(auth);
      // Navigate to the property's home, NOT "/" — the router's index route
      // (`path="/"`) renders <Navigate to="/login" />, which would bounce an
      // authenticated user to login. Added properties are MarketingOnly, so
      // their home is the marketing dashboard.
      window.location.assign("/marketing");
    },
  });
}
